import { useEffect, useRef } from 'react';

/**
 * Handle Android/iOS system back button when a modal is fullscreen on mobile.
 *
 * Usage (simple):
 *   useMobileFullscreenModalBackHandler(visible && isMobile, onClose);
 *
 * Usage (with internal navigation):
 *   useMobileFullscreenModalBackHandler(visible && isMobile, onClose, {
 *     canGoBack: () => currentStep !== 'initial',
 *     onBack: () => setCurrentStep('initial')
 *   });
 *
 * Behavior:
 * - Pushes a history state when modal opens
 * - On back button press:
 *   - If canGoBack() returns true → calls onBack() and stays in modal
 *   - Otherwise → calls onClose()
 * - Cleans up history state when modal closes via UI
 */
const useMobileFullscreenModalBackHandler = (isActive, onClose, options = {}) => {
  const hasPushedStateRef = useRef(false);
  const closedByPopStateRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const optionsRef = useRef(options);
  const modalIdRef = useRef(null);

  // Keep latest callbacks without re-subscribing listeners
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (!isActive) return;

    const uniqueId = `${Date.now()}-${Math.random()}`;
    modalIdRef.current = uniqueId;
    closedByPopStateRef.current = false;

    // Push history state
    window.history.pushState({ mobileFullscreenModal: true, modalId: uniqueId }, '');
    hasPushedStateRef.current = true;

    const handlePopState = () => {
      if (!hasPushedStateRef.current) return;

      // If we're back to our own state (returned from child modal), don't close
      if (window.history.state?.modalId === modalIdRef.current) return;

      const { canGoBack, onBack } = optionsRef.current || {};

      // Check if we should navigate within modal instead of closing
      if (typeof canGoBack === 'function' && canGoBack() && typeof onBack === 'function') {
        onBack();

        // Push new state to keep modal open
        const newId = `${Date.now()}-${Math.random()}`;
        window.history.pushState({ mobileFullscreenModal: true, modalId: newId }, '');
        modalIdRef.current = newId;
        hasPushedStateRef.current = true;
        closedByPopStateRef.current = false;
      } else {
        // Close modal
        closedByPopStateRef.current = true;
        hasPushedStateRef.current = false;
        onCloseRef.current?.();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);

      // Clean up: remove history state if modal was closed via UI (not back button)
      if (hasPushedStateRef.current && !closedByPopStateRef.current) {
        if (window.history.state?.modalId === modalIdRef.current) {
          window.history.back();
        }
      }

      hasPushedStateRef.current = false;
      closedByPopStateRef.current = false;
      modalIdRef.current = null;
    };
  }, [isActive]);
};

export default useMobileFullscreenModalBackHandler;
